// Custom Pages management panel for the Home tab. Custom Pages are additional homepages served at
// {domain}/{slug}; each has its own banners and sections. Page switching lives in the Home pivot
// tab's dropdown (StorefrontPagesEditorPage); this panel manages the page list itself
// (add / rename / enable-disable / delete). Per-page hero banner presentation (height, full width,
// image fit, under header) is edited in the banner editor's own panel on that page.
//
// Persistence: pages are a first-class API resource. Add / rename / slug / enable are committed
// immediately (they target the preview storefront, so pre-publish commits are safe) with the
// standard named-object loading toast; the API re-validates the slug and a success:false response
// surfaces as the toast error. Deleting a page cascades server-side (its sections + banners go too);
// the parent orchestrates the working-list cleanup via onDeletePage.

import React from 'react';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IconButton,
    Panel,
    PanelType,
    PrimaryButton,
    Stack,
    TextField,
    Toggle,
} from '@fluentui/react';
import { toast } from 'react-toastify';
import { IStorefrontPage } from '../../../models/Storefront';
import { useSaveStorefrontPageMutation } from '../../../store/endpoints/StorefrontPageEndpoints';
import { genPageId, slugify, sortedPages, validateSlug } from './pagesConfig';
import { DynamicSetting } from '../../Common/Setting';

interface IStorefrontManagePagesPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    storefront: string;
    // The storefront's pages (query-backed; mutations here invalidate and refetch it).
    pages: IStorefrontPage[];
    selectedPageId?: string;
    onSelectPage: (pageId?: string) => void;
    // Delete a page AND its sections/banners (parent orchestrates the full cleanup + the API call).
    onDeletePage: (pageId: string) => void;
}

const StorefrontManagePagesPanel: React.FC<IStorefrontManagePagesPanelProps> = (props) => {
    const pages = sortedPages(props.pages);
    const [saveStorefrontPage] = useSaveStorefrontPageMutation();

    const [newTitle, setNewTitle] = React.useState('');
    const [newSlug, setNewSlug] = React.useState('');
    const [newSlugTouched, setNewSlugTouched] = React.useState(false);
    // In-progress title/slug edits per page id, committed to the API on blur (so a keystroke isn't a
    // network save and the API never receives a half-typed invalid slug).
    const [titleDrafts, setTitleDrafts] = React.useState<Record<string, string>>({});
    const [slugDrafts, setSlugDrafts] = React.useState<Record<string, string>>({});
    const [pendingDelete, setPendingDelete] = React.useState<IStorefrontPage | null>(null);
    // Field values committed this session that may not have been refetched into props.pages yet. Each
    // save is a FULL-document PUT (the API replaces the page), so building it from the raw `page` prop
    // would carry a sibling field's OLD value whenever two single-field edits happen before the first
    // save's invalidation refetch lands — the second save reverts the first. We overlay these fresh
    // values on top of the (possibly stale) prop so every PUT carries the latest known siblings.
    const [pendingEdits, setPendingEdits] = React.useState<Record<string, Partial<IStorefrontPage>>>({});

    // Immediate upsert with the house save toast. Returns whether it succeeded (server validation
    // failures come back as success:false + message, surfaced as the toast error).
    const savePage = async (page: IStorefrontPage): Promise<boolean> => {
        const name = page.title || page.slug || 'page';
        const toastId = toast.loading(`Saving ${name}…`);
        try {
            const result = await saveStorefrontPage({ page, storefront: props.storefront }).unwrap();
            if (result.success === false) {
                toast.update(toastId, { type: 'error', render: result.message || `Failed to save ${name}`, isLoading: false, autoClose: 4000 });
                return false;
            }
            toast.update(toastId, { type: 'success', render: `${name} saved`, isLoading: false, autoClose: 3000 });
            return true;
        } catch {
            toast.update(toastId, { type: 'error', render: `Failed to save ${name}`, isLoading: false, autoClose: 4000 });
            return false;
        }
    };

    // The freshest known state of a page: the (possibly stale) prop overlaid with this session's
    // committed edits that props.pages hasn't caught up to yet.
    const freshest = (page: IStorefrontPage): IStorefrontPage => ({ ...page, ...pendingEdits[page.id ?? ''] });

    // Immediate single-field upsert. Merges the field onto the freshest known page (so the full-doc PUT
    // keeps the latest siblings), optimistically records it into pendingEdits so a following edit builds
    // on it, and rolls that field back out of the overlay if the save fails.
    const savePageField = async (page: IStorefrontPage, patch: Partial<IStorefrontPage>) => {
        const id = page.id as string;
        const next = { ...freshest(page), ...patch };
        setPendingEdits((e) => ({ ...e, [id]: { ...e[id], ...patch } }));
        const ok = await savePage(next);
        if (!ok) {
            setPendingEdits((e) => {
                const forId = { ...e[id] };
                for (const k of Object.keys(patch)) delete forId[k as keyof IStorefrontPage];
                const copy = { ...e };
                if (Object.keys(forId).length === 0) delete copy[id]; else copy[id] = forId;
                return copy;
            });
        }
    };

    // Auto-suggest the slug from the title until the merchant edits the slug directly.
    const effectiveNewSlug = newSlugTouched ? newSlug : slugify(newTitle);
    const newSlugError = effectiveNewSlug ? validateSlug(effectiveNewSlug, pages) : null;
    const canAdd = !!effectiveNewSlug && !newSlugError;

    const addPage = async () => {
        if (!canAdd) return;
        const maxSort = pages.reduce((m, p) => Math.max(m, p.sortOrder ?? 0), 0);
        const page: IStorefrontPage = {
            id: genPageId(),
            slug: effectiveNewSlug,
            title: newTitle.trim() || effectiveNewSlug,
            sortOrder: maxSort + 1,
        };
        const ok = await savePage(page);
        if (!ok) return;
        setNewTitle('');
        setNewSlug('');
        setNewSlugTouched(false);
        props.onSelectPage(page.id);
    };

    // Commit a title edit on blur, only if it actually changed.
    const commitTitle = (page: IStorefrontPage) => {
        if (!page.id) return;
        const draft = titleDrafts[page.id];
        if (draft === undefined) return;
        const next = draft.trim() || undefined;
        setTitleDrafts((d) => { const copy = { ...d }; delete copy[page.id as string]; return copy; });
        if ((freshest(page).title ?? undefined) !== next) void savePageField(page, { title: next });
    };

    // Commit a slug edit on blur, only if it's valid and actually changed.
    const commitSlug = (page: IStorefrontPage) => {
        if (!page.id) return;
        const draft = slugDrafts[page.id];
        if (draft === undefined) return;
        const isValid = !validateSlug(draft, pages, page.id);
        setSlugDrafts((d) => { const copy = { ...d }; delete copy[page.id as string]; return copy; });
        if (isValid && draft !== freshest(page).slug) void savePageField(page, { slug: draft });
    };

    const toggleEnabled = (page: IStorefrontPage, checked?: boolean) =>
        void savePageField(page, { enabled: checked ? undefined : false });

    const copyLink = (slug: string) => {
        const path = `/${slug}`;
        navigator.clipboard?.writeText(path).then(
            () => toast.success(`Copied ${path}`),
            () => toast.error('Could not copy link'),
        );
    };

    return (
        <>
            <Panel
                isOpen={props.isOpen}
                onDismiss={props.onDismiss}
                headerText='Manage Custom Pages'
                type={PanelType.custom}
                customWidth='480px'
                isBlocking={false}
                closeButtonAriaLabel='Close'
            >
                <Stack tokens={{ childrenGap: '1em' }} style={{ marginTop: 8 }}>
                    <span style={{ fontSize: 13, color: '#605e5c' }}>
                        Custom pages are additional homepages served at {'{domain}'}/{'{slug}'}. Each has its own banners
                        and sections. Deleting a page also deletes its banners and sections.
                    </span>

                    {/* Add page */}
                    <Stack tokens={{ childrenGap: 6 }} styles={{ root: { border: '1px solid #edebe9', borderRadius: 4, padding: '0.75em', background: '#faf9f8' } }}>
                        <span style={{ fontWeight: 600 }}>Add a page</span>
                        <TextField label='Title' value={newTitle} onChange={(_, v) => setNewTitle(v ?? '')} placeholder='e.g. About Us' />
                        <TextField
                            label='Slug'
                            value={effectiveNewSlug}
                            onChange={(_, v) => { setNewSlugTouched(true); setNewSlug(slugify(v ?? '')); }}
                            prefix='/'
                            errorMessage={newSlugError ?? undefined}
                            description='Lowercase, hyphen-separated. Auto-filled from the title until edited.'
                        />
                        <div><PrimaryButton iconProps={{ iconName: 'Add' }} text='Add Page' onClick={addPage} disabled={!canAdd} /></div>
                    </Stack>

                    {/* Existing pages */}
                    {pages.length === 0 && <span style={{ color: '#605e5c' }}>No custom pages yet.</span>}
                    {pages.map((page) => {
                        const pageId = page.id ?? '';
                        // Render against the freshest known state so an optimistic edit doesn't flicker
                        // back to the stale prop value before the invalidation refetch lands.
                        const fp = freshest(page);
                        const draftSlug = slugDrafts[pageId] ?? fp.slug;
                        const slugError = validateSlug(draftSlug, pages, page.id);
                        return (
                            <Stack key={pageId} tokens={{ childrenGap: 6 }} styles={{ root: { border: '1px solid #edebe9', borderRadius: 4, padding: '0.75em', background: '#fff' } }}>
                                <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                                    <span style={{ fontWeight: 600 }}>{fp.title || fp.slug}</span>
                                    <Stack horizontal verticalAlign='center'>
                                        <IconButton iconProps={{ iconName: 'Link' }} title={`Copy /${fp.slug}`} ariaLabel='Copy link' onClick={() => copyLink(fp.slug)} />
                                        <IconButton iconProps={{ iconName: 'Delete' }} title='Delete page' ariaLabel='Delete page' onClick={() => setPendingDelete(page)} />
                                    </Stack>
                                </Stack>
                                <DynamicSetting id={`storefront.page.${pageId}.title`}>
                                <TextField
                                    label='Title'
                                    value={titleDrafts[pageId] ?? fp.title ?? ''}
                                    onChange={(_, v) => setTitleDrafts((d) => ({ ...d, [pageId]: v ?? '' }))}
                                    onBlur={() => commitTitle(page)}
                                />
                                </DynamicSetting>
                                <DynamicSetting id={`storefront.page.${pageId}.slug`}>
                                <TextField
                                    label='Slug'
                                    prefix='/'
                                    value={draftSlug}
                                    errorMessage={slugError ?? undefined}
                                    onChange={(_, v) => setSlugDrafts((d) => ({ ...d, [pageId]: slugify(v ?? '') }))}
                                    onBlur={() => commitSlug(page)}
                                />
                                </DynamicSetting>
                                <DynamicSetting id={`storefront.page.${pageId}.enabled`}>
                                <Toggle
                                    label='Enabled'
                                    inlineLabel
                                    onText='On'
                                    offText='Off'
                                    checked={fp.enabled !== false}
                                    onChange={(_, checked) => toggleEnabled(page, checked)}
                                />
                                </DynamicSetting>
                            </Stack>
                        );
                    })}
                    <DefaultButton text='Done' onClick={props.onDismiss} />
                </Stack>
            </Panel>

            <Dialog
                hidden={!pendingDelete}
                onDismiss={() => setPendingDelete(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Delete page?',
                    subText: pendingDelete
                        ? `Deleting "${pendingDelete.title || pendingDelete.slug}" will also delete all of its banners and sections. This can't be undone.`
                        : undefined,
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text='Delete'
                        styles={{ root: { backgroundColor: '#db2828', borderColor: '#db2828' }, rootHovered: { backgroundColor: '#d01919', borderColor: '#d01919' } }}
                        onClick={() => {
                            if (pendingDelete?.id) props.onDeletePage(pendingDelete.id);
                            setPendingDelete(null);
                        }}
                    />
                    <DefaultButton text='Cancel' onClick={() => setPendingDelete(null)} />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default StorefrontManagePagesPanel;
